import type { BrandListEntry } from "@/lib/public-shop-agent-fetch";

export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
export const NON_ALPHABETIC_GROUP = "#";

export interface BrandGroup {
  letter: string;
  brands: BrandListEntry[];
}

function stripDiacritics(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function normalizeBrandSearchText(value: string): string {
  return stripDiacritics(value).toLowerCase().trim();
}

export function normalizeBrandSearchMatchText(value: string): string {
  return normalizeBrandSearchText(value).replace(/[^a-z0-9]/g, "");
}

export function getBrandGroupLetter(brandName: string): string {
  const first = stripDiacritics(brandName).charAt(0).toUpperCase();
  if (first && first >= "A" && first <= "Z") {
    return first;
  }
  return NON_ALPHABETIC_GROUP;
}

// Intentionally no dedupe for now: this directory currently mirrors upstream index output.
// If duplicates appear, that's a signal we should tighten upstream data quality and/or add
// deterministic deduping here in a follow-up.
export function normalizeAndSortBrands(brands: BrandListEntry[]): BrandListEntry[] {
  return brands
    .map((brand) => ({
      brand_name: brand.brand_name.trim(),
      brand_slug: brand.brand_slug,
    }))
    .filter((brand) => brand.brand_name.length > 0)
    .sort((a, b) => a.brand_name.localeCompare(b.brand_name, "en", { sensitivity: "base" }));
}

export function getLetterAnchorId(letter: string): string {
  return letter === NON_ALPHABETIC_GROUP ? "brand-letter-hash" : `brand-letter-${letter}`;
}

export function getHeadingId(letter: string): string {
  return `heading-${getLetterAnchorId(letter)}`;
}

export function groupBrandsByLetter(brands: BrandListEntry[]): BrandGroup[] {
  const grouped = new Map<string, BrandListEntry[]>();

  for (const brand of brands) {
    const letter = getBrandGroupLetter(brand.brand_name);
    const existing = grouped.get(letter);
    if (existing) {
      existing.push(brand);
    } else {
      grouped.set(letter, [brand]);
    }
  }

  const orderedLetters = [
    ...ALPHABET.filter((letter) => grouped.has(letter)),
    ...(grouped.has(NON_ALPHABETIC_GROUP) ? [NON_ALPHABETIC_GROUP] : []),
  ];

  return orderedLetters.map((letter) => ({
    letter,
    brands: grouped.get(letter) ?? [],
  }));
}
